import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  BATH_OPTIONS,
  BED_OPTIONS,
  formatPrice,
  formatSqft,
  RANGE_SLIDER_CSS,
  RangeSlider,
} from './CommunitiesFilters';
import {
  COMMUNITY_LIFESTYLE_OPTIONS,
  FILTER_PRICE_BOUNDS,
  FILTER_SQFT_BOUNDS,
  formatCityWithState,
  type CommunityCityOption,
  type CommunityLifestyleFilter,
  type CommunityListFilters,
} from './propertyUtils';

type PopoverKey = 'location' | 'price' | 'rooms' | 'sqft' | 'amenities';

interface CommunitiesFilterBarProps {
  filters: CommunityListFilters;
  states: { slug: string; name: string }[];
  cities: CommunityCityOption[];
  onChange: (next: CommunityListFilters) => void;
  onReset: () => void;
  onOpenAllFilters: () => void;
  /** Number of active filter groups, shown on the All filters button. */
  activeCount: number;
  search: ReactNode;
}

function toggle<T>(list: T[], value: T): T[] {
  return list.includes(value) ? list.filter((item) => item !== value) : [...list, value];
}

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      viewBox="0 0 20 20"
      className={`h-4 w-4 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden
    >
      <path d="M5 7.5 10 12.5 15 7.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function SlidersIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M4 7h10M18 7h2M4 17h2M10 17h10" />
      <circle cx="16" cy="7" r="2.5" />
      <circle cx="8" cy="17" r="2.5" />
    </svg>
  );
}

function PillToggle({ active, label, onClick }: { active: boolean; label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={`inline-flex min-h-10 items-center justify-center rounded-md border px-3 font-body text-sm transition ${
        active
          ? 'border-[rgb(var(--ala-secondary))] bg-[rgb(var(--ala-secondary))] text-[rgb(var(--ala-on-primary))]'
          : 'border-[rgb(var(--ala-border))] bg-[rgb(var(--ala-background))] text-[rgb(var(--ala-text))] hover:border-[rgb(var(--ala-text))]/30'
      }`}
    >
      {label}
    </button>
  );
}

function SectionLabel({ children, aside }: { children: ReactNode; aside?: ReactNode }) {
  return (
    <div className="mb-3 flex items-baseline justify-between gap-3">
      <p className="font-heading text-xs font-semibold uppercase tracking-wide text-[rgb(var(--ala-text-muted))]">
        {children}
      </p>
      {aside ? <p className="font-body text-xs text-[rgb(var(--ala-text))]">{aside}</p> : null}
    </div>
  );
}

function FilterPopover({
  id,
  label,
  active,
  open,
  onToggle,
  onClose,
  onClear,
  align = 'left',
  widthClass = 'w-80',
  className = '',
  children,
}: {
  id: PopoverKey;
  label: string;
  active: boolean;
  open: boolean;
  onToggle: (key: PopoverKey) => void;
  onClose: () => void;
  onClear: () => void;
  align?: 'left' | 'right';
  widthClass?: string;
  className?: string;
  children: ReactNode;
}) {
  const panelId = useId();
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) onClose();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, onClose]);

  return (
    <div ref={rootRef} className={`relative shrink-0 ${className}`}>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        aria-haspopup="dialog"
        onClick={() => onToggle(id)}
        className={`inline-flex min-h-11 max-w-[13rem] items-center gap-2 rounded-md border px-3 font-body text-sm transition ${
          active
            ? 'border-[rgb(var(--ala-secondary))] bg-[rgb(var(--ala-secondary))]/10 font-medium text-[rgb(var(--ala-text))]'
            : 'border-[rgb(var(--ala-border))] bg-[rgb(var(--ala-background))] text-[rgb(var(--ala-text))] hover:border-[rgb(var(--ala-text))]/30'
        }`}
      >
        <span className="truncate">{label}</span>
        <ChevronIcon open={open} />
      </button>

      {open ? (
        <div
          id={panelId}
          role="dialog"
          aria-label={label}
          className={`absolute top-full z-40 mt-2 ${widthClass} max-w-[calc(100vw-2rem)] rounded-xl border border-[rgb(var(--ala-border))] bg-[rgb(var(--ala-background))] shadow-[0_16px_40px_rgb(var(--ala-primary)/0.16)] ${
            align === 'right' ? 'right-0' : 'left-0'
          }`}
        >
          <div className="max-h-[min(60vh,420px)] overflow-y-auto overscroll-contain p-4">{children}</div>
          <div className="flex items-center justify-between gap-3 border-t border-[rgb(var(--ala-border))] px-4 py-3">
            <button
              type="button"
              onClick={onClear}
              disabled={!active}
              className="font-heading text-xs font-semibold uppercase tracking-wide text-[rgb(var(--ala-text-muted))] transition hover:text-[rgb(var(--ala-text))] disabled:opacity-40"
            >
              Clear
            </button>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex min-h-10 items-center justify-center rounded-md bg-[rgb(var(--ala-primary))] px-4 font-heading text-xs font-semibold uppercase tracking-wide text-[rgb(var(--ala-on-primary))] transition hover:opacity-90"
            >
              Done
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function rangeLabel(
  min: number,
  max: number,
  bounds: { min: number; max: number },
  format: (value: number) => string,
  fallback: string,
) {
  if (min <= bounds.min && max >= bounds.max) return fallback;
  if (min <= bounds.min) return `Up to ${format(max)}`;
  if (max >= bounds.max) return `${format(min)}+`;
  return `${format(min)} – ${format(max)}`;
}

function shortPrice(value: number) {
  return value >= 1000 ? `$${(value / 1000).toFixed(value % 1000 === 0 ? 0 : 1)}k` : `$${value}`;
}

export function CommunitiesFilterBar({
  filters,
  states,
  cities,
  onChange,
  onReset,
  onOpenAllFilters,
  activeCount,
  search,
}: CommunitiesFilterBarProps) {
  const [openKey, setOpenKey] = useState<PopoverKey | null>(null);
  const [cityQuery, setCityQuery] = useState('');
  const close = useMemo(() => () => setOpenKey(null), []);
  const onToggle = (key: PopoverKey) => setOpenKey((current) => (current === key ? null : key));

  const selectedCities = filters.cities ?? [];
  const selectedLifestyle = filters.lifestyle ?? [];
  const locationCount = filters.states.length + selectedCities.length;
  const priceActive = filters.priceMin > FILTER_PRICE_BOUNDS.min || filters.priceMax < FILTER_PRICE_BOUNDS.max;
  const sqftActive = filters.sqftMin > FILTER_SQFT_BOUNDS.min || filters.sqftMax < FILTER_SQFT_BOUNDS.max;
  const roomsCount = filters.bedrooms.length + filters.bathrooms.length;

  const visibleCities = useMemo(() => {
    const scoped = filters.states.length
      ? cities.filter((city) => filters.states.includes(String(city.state || '').toLowerCase()))
      : cities;
    const q = cityQuery.trim().toLowerCase();
    return q ? scoped.filter((city) => city.city.toLowerCase().includes(q)) : scoped;
  }, [cities, cityQuery, filters.states]);

  const locationLabel =
    locationCount === 0
      ? 'Location'
      : locationCount === 1
        ? filters.states.length === 1
          ? states.find((state) => state.slug === filters.states[0])?.name || 'Location'
          : (() => {
              const city = cities.find((item) => item.slug === selectedCities[0]);
              return city ? formatCityWithState(city.city, city.state) : 'Location';
            })()
        : `Location (${locationCount})`;

  const bedLabel = filters.bedrooms
    .slice()
    .sort((a, b) => a - b)
    .map((bed) => (bed === 0 ? 'Studio' : String(bed)))
    .join(', ');
  const roomsLabel =
    roomsCount === 0
      ? 'Beds & Baths'
      : [bedLabel ? `${bedLabel} Bed` : '', filters.bathrooms.length ? `${filters.bathrooms.slice().sort((a, b) => a - b).join(', ')} Bath` : '']
          .filter(Boolean)
          .join(' · ');

  const amenitiesLabel =
    selectedLifestyle.length === 0
      ? 'Amenities'
      : selectedLifestyle.length === 1
        ? COMMUNITY_LIFESTYLE_OPTIONS.find((option) => option.id === selectedLifestyle[0])?.label || 'Amenities'
        : `Amenities (${selectedLifestyle.length})`;

  return (
    <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:gap-2">
      <style>{RANGE_SLIDER_CSS}</style>
      <div className="min-w-0 lg:min-w-[220px] lg:flex-1 xl:max-w-md">{search}</div>

      <div className="flex items-center gap-2 lg:hidden">
        <button
          type="button"
          onClick={onOpenAllFilters}
          className="inline-flex min-h-11 flex-1 items-center justify-center gap-2 rounded-md border border-[rgb(var(--ala-border))] bg-[rgb(var(--ala-background))] px-4 font-heading text-xs font-semibold uppercase tracking-wide text-[rgb(var(--ala-text))]"
        >
          <SlidersIcon />
          Filters
          {activeCount > 0 ? (
            <span className="rounded-full bg-[rgb(var(--ala-primary))] px-1.5 py-0.5 font-body text-[10px] font-semibold normal-case tracking-normal text-[rgb(var(--ala-on-primary))]">
              {activeCount}
            </span>
          ) : null}
        </button>
      </div>

      <div className="hidden items-center gap-2 lg:flex" role="group" aria-label="Community filters">
        <FilterPopover
          id="location"
          label={locationLabel}
          active={locationCount > 0}
          open={openKey === 'location'}
          onToggle={onToggle}
          onClose={close}
          onClear={() => onChange({ ...filters, states: [], cities: [] })}
          widthClass="w-[22rem]"
        >
          {states.length > 0 ? (
            <div className="mb-5">
              <SectionLabel>State</SectionLabel>
              <div className="flex flex-wrap gap-2">
                {states.map((state) => (
                  <PillToggle
                    key={state.slug}
                    label={state.name}
                    active={filters.states.includes(state.slug)}
                    onClick={() => onChange({ ...filters, states: toggle(filters.states, state.slug) })}
                  />
                ))}
              </div>
            </div>
          ) : null}
          {cities.length > 0 ? (
            <div>
              <SectionLabel aside={filters.states.length ? 'In selected states' : undefined}>City</SectionLabel>
              <input
                type="search"
                value={cityQuery}
                onChange={(event) => setCityQuery(event.target.value)}
                placeholder="Find a city"
                aria-label="Find a city"
                className="mb-2 min-h-10 w-full rounded-md border border-[rgb(var(--ala-border))] bg-[rgb(var(--ala-background))] px-3 font-body text-sm text-[rgb(var(--ala-text))] outline-none focus:border-[rgb(var(--ala-secondary))]"
              />
              <ul className="max-h-52 space-y-0.5 overflow-y-auto overscroll-contain">
                {visibleCities.map((city) => {
                  const checkboxId = `bar-city-${city.slug}`;
                  return (
                    <li key={city.slug}>
                      <label
                        htmlFor={checkboxId}
                        className="flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-2 transition hover:bg-[rgb(var(--ala-surface))]"
                      >
                        <input
                          id={checkboxId}
                          type="checkbox"
                          checked={selectedCities.includes(city.slug)}
                          onChange={() => onChange({ ...filters, cities: toggle(selectedCities, city.slug) })}
                          className="h-4 w-4 accent-[rgb(var(--ala-secondary))]"
                        />
                        <span className="min-w-0 flex-1 font-body text-sm text-[rgb(var(--ala-text))]">
                          {formatCityWithState(city.city, city.state)}
                        </span>
                        <span className="font-body text-xs text-[rgb(var(--ala-text-muted))]">{city.count}</span>
                      </label>
                    </li>
                  );
                })}
                {visibleCities.length === 0 ? (
                  <li className="px-2 py-2 font-body text-sm text-[rgb(var(--ala-text-muted))]">No matching cities</li>
                ) : null}
              </ul>
            </div>
          ) : null}
        </FilterPopover>

        <FilterPopover
          id="price"
          label={rangeLabel(filters.priceMin, filters.priceMax, FILTER_PRICE_BOUNDS, shortPrice, 'Price')}
          active={priceActive}
          open={openKey === 'price'}
          onToggle={onToggle}
          onClose={close}
          onClear={() =>
            onChange({ ...filters, priceMin: FILTER_PRICE_BOUNDS.min, priceMax: FILTER_PRICE_BOUNDS.max })
          }
        >
          <SectionLabel aside={`${formatPrice(filters.priceMin)} - ${formatPrice(filters.priceMax)}`}>
            Monthly rent
          </SectionLabel>
          <RangeSlider
            min={FILTER_PRICE_BOUNDS.min}
            max={FILTER_PRICE_BOUNDS.max}
            step={50}
            valueMin={filters.priceMin}
            valueMax={filters.priceMax}
            ariaLabelMin="Minimum price"
            ariaLabelMax="Maximum price"
            onMinChange={(priceMin) => onChange({ ...filters, priceMin })}
            onMaxChange={(priceMax) => onChange({ ...filters, priceMax })}
          />
        </FilterPopover>

        <FilterPopover
          id="rooms"
          label={roomsLabel}
          active={roomsCount > 0}
          open={openKey === 'rooms'}
          onToggle={onToggle}
          onClose={close}
          onClear={() => onChange({ ...filters, bedrooms: [], bathrooms: [] })}
        >
          <div className="mb-5">
            <SectionLabel>Bedrooms</SectionLabel>
            <div className="flex flex-wrap gap-2">
              {BED_OPTIONS.map((option) => (
                <PillToggle
                  key={option.value}
                  label={option.value === 0 ? 'Studio' : String(option.value)}
                  active={filters.bedrooms.includes(option.value)}
                  onClick={() => onChange({ ...filters, bedrooms: toggle(filters.bedrooms, option.value as number) })}
                />
              ))}
            </div>
          </div>
          <div>
            <SectionLabel>Bathrooms</SectionLabel>
            <div className="flex flex-wrap gap-2">
              {BATH_OPTIONS.map((option) => (
                <PillToggle
                  key={option.value}
                  label={String(option.value)}
                  active={filters.bathrooms.includes(option.value)}
                  onClick={() => onChange({ ...filters, bathrooms: toggle(filters.bathrooms, option.value as number) })}
                />
              ))}
            </div>
          </div>
        </FilterPopover>

        <FilterPopover
          id="sqft"
          className="hidden xl:block"
          label={rangeLabel(filters.sqftMin, filters.sqftMax, FILTER_SQFT_BOUNDS, (value) => value.toLocaleString(), 'Sq Ft')}
          active={sqftActive}
          open={openKey === 'sqft'}
          onToggle={onToggle}
          onClose={close}
          onClear={() => onChange({ ...filters, sqftMin: FILTER_SQFT_BOUNDS.min, sqftMax: FILTER_SQFT_BOUNDS.max })}
        >
          <SectionLabel aside={`${filters.sqftMin.toLocaleString()} - ${formatSqft(filters.sqftMax)}`}>
            Square feet
          </SectionLabel>
          <RangeSlider
            min={FILTER_SQFT_BOUNDS.min}
            max={FILTER_SQFT_BOUNDS.max}
            step={50}
            valueMin={filters.sqftMin}
            valueMax={filters.sqftMax}
            ariaLabelMin="Minimum square feet"
            ariaLabelMax="Maximum square feet"
            onMinChange={(sqftMin) => onChange({ ...filters, sqftMin })}
            onMaxChange={(sqftMax) => onChange({ ...filters, sqftMax })}
          />
        </FilterPopover>

        <FilterPopover
          id="amenities"
          className="hidden xl:block"
          align="right"
          label={amenitiesLabel}
          active={selectedLifestyle.length > 0}
          open={openKey === 'amenities'}
          onToggle={onToggle}
          onClose={close}
          onClear={() => onChange({ ...filters, lifestyle: [] })}
        >
          <SectionLabel>What matters to you?</SectionLabel>
          <div className="flex flex-wrap gap-2">
            {COMMUNITY_LIFESTYLE_OPTIONS.map((option) => (
              <PillToggle
                key={option.id}
                label={option.label}
                active={selectedLifestyle.includes(option.id)}
                onClick={() =>
                  onChange({
                    ...filters,
                    lifestyle: toggle<CommunityLifestyleFilter>(selectedLifestyle, option.id),
                  })
                }
              />
            ))}
          </div>
        </FilterPopover>

        <button
          type="button"
          onClick={() => {
            close();
            onOpenAllFilters();
          }}
          className="inline-flex min-h-11 shrink-0 items-center gap-2 rounded-md border border-[rgb(var(--ala-border))] bg-[rgb(var(--ala-background))] px-3 font-body text-sm text-[rgb(var(--ala-text))] transition hover:border-[rgb(var(--ala-text))]/30"
        >
          <SlidersIcon />
          All filters
          {activeCount > 0 ? (
            <span className="rounded-full bg-[rgb(var(--ala-primary))] px-1.5 py-0.5 font-body text-[10px] font-semibold text-[rgb(var(--ala-on-primary))]">
              {activeCount}
            </span>
          ) : null}
        </button>

        {activeCount > 0 ? (
          <button
            type="button"
            onClick={() => {
              close();
              onReset();
            }}
            className="inline-flex min-h-11 shrink-0 items-center px-2 font-heading text-xs font-semibold uppercase tracking-wide text-[rgb(var(--ala-text-muted))] underline-offset-4 transition hover:text-[rgb(var(--ala-text))] hover:underline"
          >
            Reset
          </button>
        ) : null}
      </div>
    </div>
  );
}

export default CommunitiesFilterBar;
